import React, { useEffect, useMemo, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Combobox,
  Box as Container,
  Dialog,
  Input,
  Box as LoadingContainer,
} from '@node-space/ui-components'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import Loader from 'components/spinners/LoadingAnimationDark'
import { PathNames } from 'constants/General'
import { useGetFiatBeneficiaries } from 'hooks/fiat/useGetFiatBeneficiaries'
import useBeneficiaryPayoutDisclaimer from 'hooks/useBeneficiaryPayoutDisclaimer'
import { BeneficiaryTransferDestination, BeneficiaryType } from 'types/beneficiaries'
import { Currencies } from 'types/types'
import { roundNumberWithCommas } from 'utils/utils'
import { selectedFiatBeneficiaryAtom, selectedWalletAtom } from './atoms/atoms'
import PayCalloutsContainers from './components/PayCalloutsContainers'
import useBeneficiaryOptions from './hooks/useBeneficiaryOptions'
import useIsB2cPayoutsEnabled from './hooks/useIsB2cPayoutsEnabled'
import { PayActionProps } from './Pay'
import { resetFields, setFormDetails } from './utils/payFormUtils'

const PayFormBenficiary = ({
  form,
  loading: isLoading,
  registerFormField,
  dismissAction,
  setStep,
}: PayActionProps) => {
  const { t } = useTranslation()
  const [isHelperTextDisplayed, setIsHelperTextDisplayed] = useState(false)
  const formValues = form.watch()
  const setSelectedFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const sourceWallet = useAtomValue(selectedWalletAtom)

  const {
    fiatBeneficiaries,
    isFetching: isFetchingBeneficiaries,
    isError: isFetchingBeneficiariesError,
  } = useGetFiatBeneficiaries({
    beneficiaryType: BeneficiaryType.THIRD_PARTY,
    currency: sourceWallet?.currency,
  })

  const filteredBeneficiaryList = useMemo(() => {
    return fiatBeneficiaries?.filter(
      beneficiary => beneficiary?.currency === sourceWallet?.currency
    )
  }, [fiatBeneficiaries, sourceWallet?.currency])

  const selectedBeneficiary = useMemo(() => {
    if (!formValues?.beneficiarySelection && !filteredBeneficiaryList?.length) return

    return filteredBeneficiaryList?.find(
      beneficiary => beneficiary?.reference === formValues?.beneficiarySelection
    )
  }, [formValues?.beneficiarySelection, filteredBeneficiaryList])

  useEffect(() => {
    if (!selectedBeneficiary) return
    resetFields(form)
    setFormDetails(form, selectedBeneficiary)
    setSelectedFiatBeneficiaryAtom(selectedBeneficiary)

    form.setValue('currencyCode', selectedBeneficiary?.currency)
    form.setValue('paymentMethod', selectedBeneficiary?.paymentMethod)
    form.setValue('transferDestination', selectedBeneficiary?.transferDestination)
  }, [selectedBeneficiary, filteredBeneficiaryList])

  const isInsufficientBalance = sourceWallet?.balance <= 0

  const calloutItems = useMemo(() => {
    const callouts = []
    const currency = sourceWallet?.currency

    if (selectedBeneficiary?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL) {
      callouts.push(t('internationalPayInstruction', { currency }))
    } else if (currency === Currencies.EUR) {
      callouts.push(t('localEurPayInstruction'))
    } else if (currency === Currencies.GBP) {
      callouts.push(t('localGbpPayInstruction'))
    }

    return callouts
  }, [sourceWallet?.currency, selectedBeneficiary?.transferDestination])

  const { isB2cPayoutEnabled, isLoading: isFetchingB2CCapability } =
    useIsB2cPayoutsEnabled(sourceWallet)

  const beneficiaryOptions = useBeneficiaryOptions({
    beneficiaries: filteredBeneficiaryList,
    walletTransferDestinations: sourceWallet?.supportedTransferDestinations,
    isB2cPayoutEnabled,
  })

  useEffect(() => {
    setIsHelperTextDisplayed(sourceWallet?.supportedTransferDestinations?.length < 2)
  }, [sourceWallet])

  const selectBeneficiaryDropdownPlaceholder = useMemo(() => {
    if (filteredBeneficiaryList?.length && !beneficiaryOptions?.length) {
      return t('manageBeneficiaries.beneficiarySelect')
    } else if (!filteredBeneficiaryList?.length && !beneficiaryOptions?.length) {
      return t('manageBeneficiaries.noBeneficiaries')
    } else if (!!beneficiaryOptions?.length && !selectedBeneficiary) {
      return t('manageBeneficiaries.beneficiarySelect')
    }
  }, [beneficiaryOptions?.length, selectedBeneficiary, filteredBeneficiaryList])

  const beneficiaryListHelperText = useBeneficiaryPayoutDisclaimer(
    !!filteredBeneficiaryList?.length,
    !!beneficiaryOptions?.length
  )

  const isCalloutVisible = useMemo(() => {
    return selectedBeneficiary && !!filteredBeneficiaryList?.length && !isFetchingBeneficiariesError
  }, [selectedBeneficiary, filteredBeneficiaryList?.length, isFetchingBeneficiariesError])

  const errorCalloutMessage = useMemo(() => {
    if (isFetchingBeneficiariesError) {
      return t('manageBeneficiaries.beneficiaryFetchError')
    }
  }, [isFetchingBeneficiariesError])

  const isShowingInsufficientError = isInsufficientBalance && !errorCalloutMessage
  const isShowingErrorCalloutMessage = !!errorCalloutMessage

  const isShowingBeneficiarySelect = useMemo(() => {
    return (
      !!sourceWallet &&
      !isLoading &&
      !isFetchingBeneficiaries &&
      !!beneficiaryOptions?.length &&
      !!fiatBeneficiaries?.length &&
      !isInsufficientBalance &&
      !errorCalloutMessage &&
      !isShowingInsufficientError &&
      !isShowingErrorCalloutMessage
    )
  }, [
    sourceWallet,
    isLoading,
    isFetchingBeneficiaries,
    beneficiaryOptions?.length,
    fiatBeneficiaries?.length,
    isInsufficientBalance,
    errorCalloutMessage,
    isShowingInsufficientError,
    isShowingErrorCalloutMessage,
  ])

  const onSubmit: SubmitHandler<typeof formValues> = async () => {
    setStep('amount')
  }

  const onWalletChange = walletId => {
    form.setValue('walletId', walletId)
  }

  const onBeneficiaryChange = beneficiaryReference => {
    form.setValue('beneficiarySelection', beneficiaryReference)
  }

  const walletOptions = useMemo(() => {
    return [
      {
        LeftElement: <Avatar size="small" type="currency" name={sourceWallet?.currency} />,
        leftText: sourceWallet?.walletName,
        rightText: `${roundNumberWithCommas(sourceWallet?.balance, 2)}
          ${sourceWallet?.currency}`,
        value: sourceWallet?.isLegacyWallet
          ? sourceWallet?.legacyWalletId?.toString()
          : sourceWallet?.walletId,
      },
    ]
  }, [sourceWallet])

  const isLoadingForm = isLoading || isFetchingBeneficiaries || isFetchingB2CCapability

  return (
    <Dialog
      title={t('whoToPay')}
      open
      onClose={dismissAction}
      type="utility"
      ActionButtons={[
        <Button
          key="payRecipient"
          variant="primary"
          testId={t('payRecipient')}
          type="submit"
          loading={isLoading || isFetchingB2CCapability}
          disabled={
            !selectedBeneficiary || !filteredBeneficiaryList?.length || isInsufficientBalance
          }
          onClick={form.handleSubmit(onSubmit)}
        >
          {t('payRecipient')}
        </Button>,
        <Button key="cancel" variant="secondary" onClick={dismissAction}>
          {t('cancel')}
        </Button>,
      ]}
    >
      <form>
        <Container flexCol gapY={24}>
          <Combobox
            key={sourceWallet?.walletId}
            testId="wallet-options"
            id="wallet-options"
            label={t('formFields.payFromWallet')}
            items={walletOptions}
            defaultValue={
              sourceWallet?.isLegacyWallet
                ? sourceWallet?.legacyWalletId?.toString()
                : sourceWallet?.walletId
            }
            loading={isLoadingForm}
            onChange={onWalletChange}
            disabled
          />

          {isLoadingForm && (
            <LoadingContainer flex justify="center" paddingT={32}>
              {Loader}
            </LoadingContainer>
          )}

          {!isLoadingForm && (
            <>
              {isShowingInsufficientError && (
                <Callout variant="error">
                  {t('payInsufficient', { currencyCode: sourceWallet?.currency })}
                </Callout>
              )}

              {isShowingErrorCalloutMessage && (
                <Callout variant="error">{errorCalloutMessage}</Callout>
              )}

              {isShowingBeneficiarySelect ? (
                <Combobox
                  testId="beneficiary-options"
                  id="beneficiary-options"
                  label={t('manageBeneficiaries.selectBeneficiary')}
                  items={beneficiaryOptions}
                  defaultValue={formValues?.beneficiarySelection}
                  onChange={onBeneficiaryChange}
                  helperText={isHelperTextDisplayed ? beneficiaryListHelperText : ''}
                  selectPlaceholder={selectBeneficiaryDropdownPlaceholder}
                />
              ) : (
                !isShowingErrorCalloutMessage &&
                !isShowingInsufficientError && (
                  <Callout variant="warning">
                    <Box flexCol gapY={12}>
                      {t('payouts.noSupportedBeneficiaries')}
                      <ExternalLink
                        className="text-secondary-500 hover:text-secondary-700"
                        target="_top"
                        href={`${PathNames.BANK_BENEFICIARIES}?callbackUrl=${location.pathname}`}
                      >
                        {t('addNewBeneficiaryWithoutPlus')}
                      </ExternalLink>
                    </Box>
                  </Callout>
                )
              )}
              {isCalloutVisible && (
                <>
                  <Input
                    {...registerFormField('reference')}
                    placeholder={t('inputPlaceholders.beneficiaryPaymentReferencePlaceHolder')}
                    value={formValues?.reference}
                    onChange={e => form.setValue('reference', e.target.value)}
                  />
                  <PayCalloutsContainers calloutItems={calloutItems} />
                </>
              )}
            </>
          )}
        </Container>
      </form>
    </Dialog>
  )
}

export default React.memo(PayFormBenficiary)
